import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import { openStream } from '../lib/sse';
import { onUnauthorized } from '../lib/updating';
import type { SseHandle } from '../lib/sse';
import type { Session, SseEvent, TraceEntry } from '../types';
import { reducer } from './reducer';
import { initialState } from './state';
import type { Action, AppState, ModalState, ToastItem } from './state';

interface Ctx {
  state: AppState;
  dispatch: (a: Action) => void;
  toast: (t: Omit<ToastItem, 'id'>) => void;
  openModal: (m: ModalState) => void;
  closeModal: () => void;
  reload: () => void;
  reconnect: () => void;
}

const StoreContext = createContext<Ctx | null>(null);

let toastSeq = 0;

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const stream = useRef<SseHandle | null>(null);

  const toast = useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = 'to' + ++toastSeq;
    dispatch({ type: 'toast/push', toast: { ...t, id } });
    if (t.kind !== 'danger') {
      window.setTimeout(() => dispatch({ type: 'toast/close', id }), 6000);
    }
  }, []);

  const openModal = useCallback((m: ModalState) => dispatch({ type: 'modal/open', modal: m }), []);
  const closeModal = useCallback(() => dispatch({ type: 'modal/close' }), []);

  const reload = useCallback(() => {
    dispatch({ type: 'load/start' });
    Promise.all([api.sessions(), api.messages(), api.trace(), api.blackboard(), api.claims()])
      .then(([sessions, messages, trace, blackboard, claims]) => {
        dispatch({ type: 'load/done', data: { sessions, messages, trace, blackboard, claims } });
      })
      .catch((e: unknown) =>
        dispatch({ type: 'load/error', error: e instanceof Error ? e.message : 'errore ignoto' }),
      );
  }, []);

  // sessione: finché è ignota non si lampeggia la shell (§6.0)
  useEffect(() => {
    api
      .sessionInfo()
      .then((info) => dispatch({ type: 'auth/info', info }))
      .catch(() => dispatch({ type: 'auth/info', info: null }));
  }, []);

  // una sessione scaduta o chiusa altrove: si torna al login
  useEffect(() => onUnauthorized(() => dispatch({ type: 'auth/logout' })), []);

  // il registro si mostra anche senza provider: è il lavoro, non dipende da chi lo fa
  useEffect(() => {
    if (state.authed) reload();
  }, [state.authed, reload]);

  // le sessioni che la plancia conosce: un evento di una sconosciuta la fa leggere dal registro
  const known = useRef(new Set<string>());
  useEffect(() => {
    for (const x of state.sessions) known.current.add(x.id);
  }, [state.sessions]);

  const onEvent = useCallback(
    (e: SseEvent) => {
      switch (e.type) {
        case 'toast':
          toast({
            kind: e.payload.kind,
            title: e.payload.title,
            body: e.payload.body,
            actionLabel: e.payload.action ? 'Apri' : undefined,
            actionTarget: e.payload.action,
          });
          break;
        case 'session_state':
          // una sessione nata altrove (un'attività a orario): si legge dal registro e si aggiunge
          if (!known.current.has(e.payload.sessionId)) {
            known.current.add(e.payload.sessionId);
            const id = e.payload.sessionId;
            void api.sessions().then((all) => {
              const born = all.find((x) => x.id === id);
              if (born) dispatch({ type: 'session/add', session: born });
            });
            break;
          }
          dispatch({
            type: 'session/patch',
            id: e.payload.sessionId,
            patch: { state: e.payload.state, pending: e.payload.pending ?? null },
          });
          break;
        case 'message':
          dispatch({ type: 'message/add', message: e.payload.message });
          break;
        case 'mission_task':
          dispatch({ type: 'task/upsert', task: e.payload.task });
          break;
        case 'workspace_file':
          dispatch({
            type: 'file/change',
            sessionId: e.payload.sessionId,
            removed: e.payload.action === 'delete',
            file: {
              path: e.payload.path,
              content: e.payload.content,
              bytes: e.payload.bytes,
              updatedAt: e.payload.at,
            },
          });
          break;
        case 'trace': {
          const p = e.payload as Partial<TraceEntry>;
          dispatch({
            type: 'trace/add',
            entry: {
              id: p.id ?? 'tr-live-' + Math.random().toString(36).slice(2),
              seq: Date.now(),
              at: new Date().toISOString(),
              ...p,
            } as TraceEntry,
          });
          break;
        }
        case 'blackboard':
          dispatch({ type: 'blackboard/add', entry: e.payload.entry });
          break;
        case 'claim':
          dispatch({ type: 'claim/upsert', claim: e.payload.claim });
          break;
        case 'runtime_patch':
          if (e.payload.poll) {
            reload();
            break;
          }
          dispatch({ type: 'runtime/patch', patch: e.payload });
          break;
        case 'cost':
          dispatch({
            type: 'session/patch',
            id: e.payload.sessionId,
            patch: { tokens: e.payload.tokens },
          });
          break;
        default:
          break;
      }
    },
    [reload, toast],
  );

  useEffect(() => {
    if (!state.authed) return undefined;
    stream.current = openStream(onEvent, (s) => dispatch({ type: 'sse/status', status: s }));
    return () => {
      stream.current?.close();
      stream.current = null;
    };
  }, [state.authed, onEvent]);

  const reconnect = useCallback(() => stream.current?.reconnect(), []);

  // titolo del documento (§8): (2) PopTown quando ci sono sessioni in attesa
  const waiting = state.sessions.filter(
    (s) =>
      s.state === 'awaiting_human' || s.state === 'awaiting_clarification' || s.state === 'blocked',
  ).length;
  useEffect(() => {
    document.title = waiting > 0 ? `(${waiting}) PopTown` : 'PopTown';
  }, [waiting]);

  const value = useMemo<Ctx>(
    () => ({ state, dispatch, toast, openModal, closeModal, reload, reconnect }),
    [state, toast, openModal, closeModal, reload, reconnect],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): Ctx {
  const c = useContext(StoreContext);
  if (!c) throw new Error('useStore fuori da StoreProvider');
  return c;
}

export function useSession(id: string | undefined): Session | undefined {
  const { state } = useStore();
  return state.sessions.find((s) => s.id === id);
}
